import { zodResolver } from "@hookform/resolvers/zod";
import { newPasswordSchema } from "@station/contracts";
import { Alert, Button, FormField, Input, Spinner } from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/auth-layout.js";
import { api } from "../lib/api.js";
import { useAuthStatus } from "../lib/auth-store.js";
import { applyFieldErrors, errorMessage } from "../lib/errors.js";
import { bootstrapSession, login, switchTenant } from "../lib/session.js";

export const Route = createFileRoute("/davet")({
  validateSearch: z.object({ token: z.string().optional() }),
  beforeLoad: () => bootstrapSession().then(() => undefined),
  component: InvitationPage,
});

const newAccountSchema = z
  .object({
    fullName: z.string().trim().min(1, "Ad soyad zorunludur").max(200),
    password: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.password === value.confirmPassword, {
    path: ["confirmPassword"],
    message: "Parolalar eşleşmiyor",
  });
type NewAccount = z.infer<typeof newAccountSchema>;

function InvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const signedIn = useAuthStatus() === "authenticated";
  const [formError, setFormError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  const lookup = useQuery({
    queryKey: ["invitation", token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async () =>
      (await api.GET("/invitations/lookup", { params: { query: { token: token! } } })).data!,
  });
  const form = useForm<NewAccount>({
    resolver: zodResolver(newAccountSchema),
    defaultValues: { fullName: "", password: "", confirmPassword: "" },
  });

  async function accept(body: { fullName?: string; password?: string }, email: string): Promise<void> {
    setFormError(null);
    setAccepting(true);
    try {
      const { data } = await api.POST("/invitations/accept", { body: { token: token!, ...body } });
      if (data!.accountCreated && body.password) await login({ email, password: body.password });
      else await switchTenant(data!.tenantId);
      await navigate({ to: "/" });
    } catch (error) {
      const unmatched = applyFieldErrors(error, form.setError, ["fullName", "password"]);
      if (unmatched.length > 0 || Object.keys(form.formState.errors).length === 0)
        setFormError(errorMessage(error));
    } finally {
      setAccepting(false);
    }
  }

  if (!token || lookup.isError) {
    return (
      <AuthLayout
        title={t("auth.invite.title")}
        footer={
          <Link to="/giris" className="text-primary hover:underline">
            {t("auth.backToLogin")}
          </Link>
        }
      >
        <Alert tone="danger" title={t("auth.invite.invalidTitle")}>
          {t("auth.invite.invalidDescription")}
        </Alert>
      </AuthLayout>
    );
  }
  if (!lookup.data) {
    return (
      <AuthLayout title={t("auth.invite.title")}>
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  const invitation = lookup.data;
  const summary = t("auth.invite.summary", {
    tenant: invitation.tenantName,
    roles: invitation.roleNames.join(", "),
  });

  // Mevcut hesap: davet YALNIZCA o hesabın oturumuyla kabul edilebilir.
  if (invitation.accountExists) {
    return (
      <AuthLayout title={t("auth.invite.title")} description={summary}>
        <div className="grid gap-4">
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          {signedIn ? (
            <Button loading={accepting} onClick={() => void accept({}, invitation.email)}>
              {t("auth.invite.acceptExisting")}
            </Button>
          ) : (
            <>
              <Alert tone="info">{t("auth.invite.loginFirst", { email: invitation.email })}</Alert>
              <Button asChild>
                <Link to="/giris" search={{ redirect: `/davet?token=${encodeURIComponent(token)}` }}>
                  {t("auth.login.submit")}
                </Link>
              </Button>
            </>
          )}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.invite.title")} description={summary}>
      <form
        onSubmit={(event) =>
          void form.handleSubmit((values) =>
            accept({ fullName: values.fullName, password: values.password }, invitation.email),
          )(event)
        }
        className="grid gap-4"
        noValidate
      >
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField label={t("auth.email")}>
          {(control) => <Input {...control} value={invitation.email} readOnly autoComplete="username" />}
        </FormField>
        <FormField label={t("auth.fullName")} error={form.formState.errors.fullName?.message} required>
          {(control) => <Input {...control} {...form.register("fullName")} autoComplete="name" autoFocus />}
        </FormField>
        <FormField
          label={t("auth.newPassword")}
          description={t("auth.passwordHint")}
          error={form.formState.errors.password?.message}
          required
        >
          {(control) => (
            <Input {...control} {...form.register("password")} type="password" autoComplete="new-password" />
          )}
        </FormField>
        <FormField
          label={t("auth.confirmPassword")}
          error={form.formState.errors.confirmPassword?.message}
          required
        >
          {(control) => (
            <Input
              {...control}
              {...form.register("confirmPassword")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </FormField>
        <Button type="submit" loading={accepting}>
          {t("auth.invite.acceptNew")}
        </Button>
      </form>
    </AuthLayout>
  );
}
